import { ApolloClient } from '@apollo/client';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getUsersForBusinessEntityIdQuery,
  GetUsersForBusinessEntityIdResult,
  User
} from '../../gql/queries/getUsersForBusinessEntityIdQuery';
import {
  getUsersWithSmartAccess,
  listPrincipalsQuery,
  ListPrincipalsResult,
  PrincipalActor,
  SMART_PERMISSION_ID
} from '../../gql/queries/listPrincipalsQuery';
import { AppDispatch, RootState } from '../../store';
import { MLACActionClassTypes } from '../pages/RFXRequest/steps/RFXRequestSuppliersStep/types';

export interface GetUsersForBusinessEntityIdState {
  isLoading: boolean;
  errorMessage: string;
  fetchedUsers: User[];
}

export const initialState: GetUsersForBusinessEntityIdState = {
  isLoading: false,
  errorMessage: '',
  fetchedUsers: []
};

const getUsersForBusinessEntityIdSlice = createSlice({
  name: 'getUsersForBusinessEntityId',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    setFetchedUsers: (state, action) => {
      state.fetchedUsers = action.payload;
    },
    resetState: () => initialState
  }
});

export const { setIsLoading, setErrorMessage, setFetchedUsers } = getUsersForBusinessEntityIdSlice.actions;

export const selectIsLoading = (state: RootState) => state.getUsersForBusinessEntityId.isLoading;

export const selectErrorMessage = (state: RootState) => state.getUsersForBusinessEntityId.errorMessage;

export const selectFetchedUsers = (state: RootState) => state.getUsersForBusinessEntityId.fetchedUsers;

/**
 * Async thunk for fetching users for a business entity
 *
 *
 * @param {Object} params - The parameters for the thunk
 * @param {ApolloClient<object>} params.apolloClient - The Apollo Client instance
 * @param {string} params.supplierId - The ID of the supplier (business entity)
 * @param {boolean} params.isFilterEnabled - Whether to filter users with smart access
 * @param {Object} thunkAPI - The thunk API object
 * @param {AppDispatch} thunkAPI.dispatch - The dispatch function
 * @returns {Promise<void>} A promise that resolves when the operation is completed.
 */
export const getUsersForBusinessEntityId = createAsyncThunk(
  'users/getUsersForBusinessEntityId',
  async (
    {
      apolloClient,
      supplierId,
      isFilterEnabled
    }: { apolloClient: ApolloClient<object>; supplierId: string; isFilterEnabled: boolean },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;
    try {
      dispatch(setIsLoading(true));
      // Fetch existing users for the business entity
      const getUsersForBusinessEntityIdResult: {
        data: { getUsersForBusinessEntityId: GetUsersForBusinessEntityIdResult };
      } = await apolloClient.query({
        query: getUsersForBusinessEntityIdQuery,
        variables: {
          businessEntityId: supplierId,
          maxResults: 100
        },
        fetchPolicy: 'network-only'
      });
      const fetchedUsers: User[] = getUsersForBusinessEntityIdResult.data.getUsersForBusinessEntityId.users!;

      if (!isFilterEnabled) {
        dispatch(setFetchedUsers(fetchedUsers));
        return;
      }

      const listPrincipalsResult: { data: { listPrincipals: ListPrincipalsResult } } = await apolloClient.query({
        query: listPrincipalsQuery,
        variables: {
          input: {
            permissionActionClass: {
              permissionId: SMART_PERMISSION_ID,
              actionClass: MLACActionClassTypes.READ
            }
          }
        },
        fetchPolicy: 'network-only'
      });
      const actorsWithSmartAccess: PrincipalActor[] = listPrincipalsResult.data.listPrincipals.principals;
      const usersWithSmartAccess: User[] = getUsersWithSmartAccess(fetchedUsers, actorsWithSmartAccess);
      dispatch(setFetchedUsers(usersWithSmartAccess));
      return;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setErrorMessage(error.message));
      } else {
        dispatch(setErrorMessage('Something went wrong.'));
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  }
);

export default getUsersForBusinessEntityIdSlice.reducer;
